import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { ProductApiAdapter } from '../adapters/ProductApiAdapter';
import { Product, ProductFilters } from '../models/Product';
import { ProductService } from '../services/ProductService';

export const useProducts = (filters: ProductFilters = {}, queryOptions?: { enabled?: boolean }) => {
  // 🎯 Memoizar instancias del servicio para evitar recrearlas en cada render
  const productService = useMemo(() => {
    const httpClient = createHttpClient(apiClientWH);
    const apiAdapter = new ProductApiAdapter(httpClient);
    return new ProductService(apiAdapter);
  }, []);

  // 🎯 Crear query key basada en los filtros
  const queryKey = useMemo(() => {
    return ['products', filters];
  }, [filters]);

  const {
    data: products = [],
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery<Product[], Error>({
    queryKey,
    queryFn: () => productService.getProducts(filters),
    staleTime: 5 * 60 * 1000, // 5 minutos de cache
    retry: 2,
    retryDelay: 1000,
    enabled: queryOptions?.enabled ?? true,
  });

  return {
    products,
    isLoading,
    isFetching,
    error,
    refetch,
  };
};

// 🎯 Hook para obtener un producto específico por ID
export const useProduct = (id: number) => {
  const productService = useMemo(() => {
    const httpClient = createHttpClient(apiClientWH);
    const apiAdapter = new ProductApiAdapter(httpClient);
    return new ProductService(apiAdapter);
  }, []);

  const {
    data: product,
    isLoading,
    error,
    refetch,
  } = useQuery<Product, Error>({
    queryKey: ['product', id],
    queryFn: () => productService.getProductById(id),
    enabled: !!id && id > 0,
    staleTime: 10 * 60 * 1000, // 10 minutos de cache para productos individuales
    retry: 2,
  });

  return {
    product,
    isLoading,
    error,
    refetch,
  };
};

// 🎯 Hook para buscar productos con debounce via API
export const useProductSearchAPI = (searchTerm: string, debounceMs: number = 300) => {
  const productService = useMemo(() => {
    const httpClient = createHttpClient(apiClientWH);
    const apiAdapter = new ProductApiAdapter(httpClient);
    return new ProductService(apiAdapter);
  }, []);

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearchTerm(searchTerm), debounceMs);
    return () => window.clearTimeout(id);
  }, [searchTerm, debounceMs]);

  const {
    data: products = [],
    isLoading,
    error,
    refetch,
  } = useQuery<Product[], Error>({
    queryKey: ['products', 'search', debouncedSearchTerm],
    queryFn: () => productService.searchProducts(debouncedSearchTerm),
    enabled: !!debouncedSearchTerm && debouncedSearchTerm.length >= 2,
    staleTime: 2 * 60 * 1000, // 2 minutos de cache para búsquedas
  });

  return {
    products,
    isLoading,
    error,
    refetch,
  };
};
